import { Link } from "react-router-dom";
import { useSeo } from "../lib/useSeo";
import { Mail, ShieldCheck } from "lucide-react";

const SITE_URL = "https://railwaycareers.co.uk";
const DPO_EMAIL = "datacontroller@railwaycareers.co.uk";
const LAST_UPDATED = "29 April 2026";

const SECTIONS = [
  {
    id: "who-we-are",
    h: "1. Who we are",
    body: [
      "Railway Careers UK (\"we\", \"us\", \"our\") operates the website railwaycareers.co.uk. We are an independent UK rail recruitment platform.",
      `For the purposes of the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, the Data Controller for personal data collected through this website is Railway Careers UK. You can contact our Data Controller at ${DPO_EMAIL}.`,
    ],
  },
  {
    id: "what-we-collect",
    h: "2. What personal data we collect",
    body: [
      "We only collect what we need to run the service. The categories of personal data we may process are:",
    ],
    list: [
      "**Account data** (employers and admins): name, email address, company name, password (hashed using bcrypt — we never store plain-text passwords).",
      "**Newsletter / job-alert data** (job seekers): email address, optional first name, optional interests/operator preferences if you sign up for targeted alerts.",
      "**Vacancy data** (employers): job title, description, location, salary, application URL and any other content you choose to publish on a vacancy listing.",
      "**Postcode data** (job seekers using the Vacancy Map): the UK postcode you enter is sent to the postcodes.io API to obtain coordinates for distance calculation. We do not retain your postcode after the request completes.",
      "**Technical data**: IP address, browser type, device type, referring URL, pages visited, timestamps, error logs. Used for security, abuse prevention and analytics.",
      "**Cookies and similar technologies**: see section 7 below.",
    ],
  },
  {
    id: "lawful-basis",
    h: "3. Our lawful basis for processing",
    body: ["We rely on the following lawful bases under UK GDPR Article 6:"],
    list: [
      "**Contract** — to operate your employer or admin account, post and manage vacancies.",
      "**Consent** — to send the Monday Briefing newsletter and operator-specific job alerts. You can withdraw consent at any time via the one-click unsubscribe link in every email.",
      "**Legitimate interests** — to operate the platform securely, prevent fraud, debug errors, generate aggregated analytics and improve the service.",
      "**Legal obligation** — where we are required to retain or disclose data by UK law.",
    ],
  },
  {
    id: "how-we-use",
    h: "4. How we use your data",
    list: [
      "Provide and maintain the Railway Careers UK service.",
      "Authenticate employer and admin accounts.",
      "Send transactional emails (account verification, password reset, vacancy expiry reminders).",
      "Send the Monday Briefing newsletter and operator-specific job alerts (consent only).",
      "Calculate driving distances on the Vacancy Map (transient — postcode is not stored).",
      "Detect and prevent fraud, abuse, scraping and security incidents.",
      "Comply with legal obligations and respond to lawful requests from authorities.",
    ],
  },
  {
    id: "third-parties",
    h: "5. Third-party services we share data with",
    body: [
      "We keep this list deliberately short. We do not sell your data to anyone, ever.",
    ],
    list: [
      "**Resend** (email delivery) — receives email address and message content for transactional emails and the newsletter. Resend is hosted in the EU. https://resend.com/privacy",
      "**Adzuna** (jobs aggregation) — we *receive* public UK rail vacancy data from Adzuna. We do not send personal data to Adzuna.",
      "**postcodes.io** (postcode geocoding) — receives the UK postcode you enter for the Vacancy Map. The postcode is not retained by us. https://postcodes.io/",
      "**OpenStreetMap / OSRM** (map tiles and routing) — your IP address is visible to OpenStreetMap when map tiles load. https://wiki.osmfoundation.org/wiki/Privacy_Policy",
      "**PostHog** (product analytics) — receives anonymised event data and a session ID. We do not pass email addresses or names to PostHog. https://posthog.com/privacy",
      "**MongoDB Atlas / hosting provider** — our database and hosting infrastructure. Data is stored in the UK or EEA.",
    ],
  },
  {
    id: "retention",
    h: "6. How long we keep your data",
    list: [
      "**Account data**: kept for as long as your account is active. We delete accounts on request within 30 days, except where we must retain limited records for legal compliance.",
      "**Vacancy data**: retained while live, then archived for up to 24 months for reporting purposes before being permanently deleted.",
      "**Newsletter subscribers**: kept until you unsubscribe. We delete inactive subscribers (no opens for 18+ months) automatically.",
      "**Technical logs**: 90 days, then permanently deleted.",
      "**Postcode lookups**: not retained — discarded after each Vacancy Map calculation.",
    ],
  },
  {
    id: "cookies",
    h: "7. Cookies and similar technologies",
    body: [
      "We use a small number of cookies and similar storage technologies. The cookie banner you see on first visit lets you accept all or essential-only.",
    ],
    list: [
      "**Essential** (always on) — session/auth cookies for employer & admin sign-in (cannot be disabled if you wish to use those features). LocalStorage is used to remember your theme preference (light/dark).",
      "**Analytics** (optional) — PostHog uses cookies / localStorage to record anonymised pageviews and feature usage. Disabled if you choose 'Essential only' on the banner.",
      "We do not use advertising cookies, third-party tracking pixels or social-media remarketing tags.",
    ],
  },
  {
    id: "your-rights",
    h: "8. Your rights",
    body: [
      "Under UK GDPR you have the following rights regarding your personal data:",
    ],
    list: [
      "**Right of access** — request a copy of the personal data we hold about you.",
      "**Right to rectification** — ask us to correct data that is inaccurate or incomplete.",
      "**Right to erasure (\"right to be forgotten\")** — ask us to delete your data, subject to legal exceptions.",
      "**Right to restrict processing** — ask us to pause processing in certain circumstances.",
      "**Right to data portability** — request your data in a machine-readable format.",
      "**Right to object** — object to processing based on legitimate interests, including direct marketing.",
      "**Right to withdraw consent** — at any time, for any consent-based processing (e.g. newsletter).",
      "**Right to lodge a complaint** with the Information Commissioner's Office (ICO) at ico.org.uk if you believe we have mishandled your data.",
    ],
    body2: [
      `To exercise any of these rights, email ${DPO_EMAIL}. We will respond within one calendar month.`,
    ],
  },
  {
    id: "security",
    h: "9. How we protect your data",
    list: [
      "All traffic to and from the site is encrypted via HTTPS (TLS 1.3).",
      "Passwords are stored using bcrypt one-way hashing.",
      "Database access is restricted to a small number of named individuals on least-privilege principles.",
      "Sensitive endpoints are rate-limited and brute-force protected.",
      "We log security events and review them regularly.",
    ],
  },
  {
    id: "children",
    h: "10. Children",
    body: [
      "Railway Careers UK is intended for adult job seekers aged 16 and above. We do not knowingly collect data from anyone under 16. If you believe a child has submitted personal data, please contact us and we will delete it.",
    ],
  },
  {
    id: "international",
    h: "11. International transfers",
    body: [
      "Personal data is processed in the UK or European Economic Area (EEA). Where any third party we use processes data outside the UK/EEA (e.g. PostHog), it is covered by appropriate safeguards including UK International Data Transfer Agreements (IDTAs) or Standard Contractual Clauses (SCCs).",
    ],
  },
  {
    id: "changes",
    h: "12. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. The \"Last updated\" date at the top of this page reflects the most recent revision. Material changes will be communicated via email to registered users where appropriate.",
    ],
  },
  {
    id: "contact",
    h: "13. Contact us",
    body: [
      `For any questions about this Privacy Policy or how we handle your data, contact our Data Controller at ${DPO_EMAIL}.`,
      "If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):",
    ],
    list: [
      "Website: ico.org.uk",
      "Helpline: 0303 123 1113",
      "Address: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF",
    ],
  },
];

function Bullet({ html }) {
  return (
    <li className="flex gap-3 text-[15px]">
      <span className="text-amber-700 mt-1.5 shrink-0">•</span>
      <span className="text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: html.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-ink">$1</strong>') }}
      />
    </li>
  );
}

export default function Privacy() {
  useSeo({
    title: "Privacy Policy | Railway Careers UK",
    description:
      "Railway Careers UK Privacy Policy — how we collect, use, store and protect your personal data under UK GDPR. Independent UK rail recruitment platform.",
    canonical: "/privacy",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Privacy Policy — Railway Careers UK",
      url: `${SITE_URL}/privacy`,
      lastReviewed: "2026-04-29",
      mainContentOfPage: {
        "@type": "WebPageElement",
        cssSelector: "main",
      },
    },
  });

  return (
    <div className="bg-cream min-h-screen" data-testid="privacy-page">
      <section className="bg-navy text-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" /> Legal
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.1] mt-3">
            Privacy Policy
          </h1>
          <p className="text-bone/70 mt-4 text-sm">
            Last updated: <span className="font-mono text-bone">{LAST_UPDATED}</span>
          </p>
          <a
            href={`mailto:${DPO_EMAIL}`}
            className="mt-6 inline-flex items-center gap-2 text-sm bg-bone/5 border border-amber/30 hover:border-amber rounded-md px-3 py-2 transition-colors"
            data-testid="privacy-dpo-email"
          >
            <Mail className="w-4 h-4 text-amber" />
            <span className="font-mono">{DPO_EMAIL}</span>
          </a>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16 grid md:grid-cols-[200px_1fr] gap-10">
        {/* Sticky TOC */}
        <aside className="md:sticky md:top-24 md:self-start hidden md:block">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink font-bold mb-4">Contents</div>
          <ul className="space-y-2 text-[13px]">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-muted-foreground hover:text-ink transition-colors">
                  {s.h}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <article className="space-y-12 prose-slate max-w-none">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-32">
              <h2 className="font-display font-bold text-ink text-2xl tracking-tight">{s.h}</h2>
              {s.body?.map((p, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed mt-3 text-[15px]">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((item, i) => <Bullet key={i} html={item} />)}
                </ul>
              )}
              {s.body2?.map((p, i) => (
                <p key={`b2-${i}`} className="text-muted-foreground leading-relaxed mt-3 text-[15px]">{p}</p>
              ))}
            </section>
          ))}

          <div className="border-t border-border pt-8">
            <Link to="/" className="text-sm text-muted-foreground hover:text-ink underline">← Back to Railway Careers UK</Link>
          </div>
        </article>
      </main>
    </div>
  );
}
